/**
 * Template Registry
 *
 * Este archivo centraliza el registro de todos los templates disponibles
 * en el sistema de Link Pago.
 *
 * Cada template debe exportar un componente Vue que cumpla con la interfaz:
 * - Props: Recibe toda la configuración y estado necesario
 * - Events: Emite eventos para actualización de campos y submit
 *
 * Los templates se cargan dinámicamente bajo demanda (lazy loading)
 * mediante el composable useTemplateLoader.
 *
 * El sistema soporta dos tipos de templates:
 * - Page Templates: Para la página de pago (Page.vue)
 * - Builder Templates: Para el generador de enlaces (Builder.vue)
 */

export interface TemplateInfo {
    name: string;
    displayName: string;
    description: string;
    author?: string;
    version?: string;
}

export type TemplateType = 'page' | 'builder';

/**
 * Metadata de los Page Templates disponibles (para Page.vue)
 */
export const pageTemplateMetadata: Record<string, TemplateInfo> = {
    default: {
        name: 'default',
        displayName: 'Default Template',
        description: 'Template estándar de Hotelpay con diseño limpio y responsive',
        author: 'Hotelpay',
        version: '1.0.0'
    },
    viaggio: {
        name: 'viaggio',
        displayName: 'Viaggio Resort Mazatlán',
        description: 'Template personalizado para Viaggio Resort con branding exclusivo',
        author: 'Hotelpay',
        version: '1.0.0'
    }
};

/**
 * Metadata de los Builder Templates disponibles (para Builder.vue)
 */
export const builderTemplateMetadata: Record<string, TemplateInfo> = {
    default: {
        name: 'default',
        displayName: 'Default Builder',
        description: 'Builder estándar de Hotelpay con todas las funcionalidades',
        author: 'Hotelpay',
        version: '1.0.0'
    },
    viaggio: {
        name: 'viaggio',
        displayName: 'Viaggio Builder',
        description: 'Builder simplificado para Viaggio Resort',
        author: 'Hotelpay',
        version: '1.0.0'
    }
};

/**
 * Alias para compatibilidad con código existente
 * @deprecated Use pageTemplateMetadata instead
 */
export const templateMetadata = pageTemplateMetadata;

/**
 * Obtiene la información de un template
 * @param templateName Nombre del template
 * @param type Tipo de template ('page' | 'builder'), default: 'page'
 */
export function getTemplateInfo(templateName: string, type: TemplateType = 'page'): TemplateInfo | null {
    const metadata = type === 'builder' ? builderTemplateMetadata : pageTemplateMetadata;
    return metadata[templateName] || null;
}

/**
 * Obtiene todos los templates disponibles
 * @param type Tipo de template ('page' | 'builder'), default: 'page'
 */
export function getAllTemplates(type: TemplateType = 'page'): TemplateInfo[] {
    const metadata = type === 'builder' ? builderTemplateMetadata : pageTemplateMetadata;
    return Object.values(metadata);
}

/**
 * Verifica si un template existe
 * @param templateName Nombre del template
 * @param type Tipo de template ('page' | 'builder'), default: 'page'
 */
export function templateExists(templateName: string, type: TemplateType = 'page'): boolean {
    const metadata = type === 'builder' ? builderTemplateMetadata : pageTemplateMetadata;
    return templateName in metadata;
}

/**
 * Obtiene el path del componente template
 * @param templateName Nombre del template
 * @param type Tipo de template ('page' | 'builder')
 */
export function getTemplatePath(templateName: string, type: TemplateType): string {
    const componentName = type === 'builder' ? 'BuilderTemplate' : 'PageTemplate';
    return `../templates/${templateName}/${componentName}.vue`;
}
